import React, { useState } from 'react';
import { 
  BarChart3, 
  Calendar, 
  Download,
  TrendingUp,
  Clock,
  FileText
} from 'lucide-react';
import { ClientDashboardLayout } from './ClientDashboardLayout';
import { TimeTrackingAnalytics } from '../../components/TimeTracker';
import { useAuthStore } from '../auth/authStore';

export const ClientReports: React.FC = () => {
  const { user } = useAuthStore();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    endDate: new Date()
  });

  const handleDateRangeChange = (range: { startDate: Date; endDate: Date }) => {
    setDateRange(range);
  };

  return (
    <ClientDashboardLayout currentPage="reports">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-gray-600 mt-1">View detailed insights into your time tracking and productivity</p>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Time Tracking</p>
                <p className="text-lg font-semibold text-gray-900">Detailed Analytics</p>
                <p className="text-sm text-gray-500 mt-1">View your time patterns and productivity trends</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Productivity</p>
                <p className="text-lg font-semibold text-gray-900">Performance Insights</p>
                <p className="text-sm text-gray-500 mt-1">Track your efficiency over time</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Export</p>
                <p className="text-lg font-semibold text-gray-900">Data Export</p>
                <p className="text-sm text-gray-500 mt-1">Download your time tracking data</p>
              </div>
              <Download className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Report Period</h3>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={dateRange.startDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({
                  ...prev,
                  startDate: new Date(e.target.value)
                }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={dateRange.endDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange(prev => ({
                  ...prev,
                  endDate: new Date(e.target.value)
                }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setDateRange({
                  startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                  endDate: new Date()
                })}
                className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Last 7 days
              </button>
              <button
                onClick={() => setDateRange({
                  startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                  endDate: new Date()
                })}
                className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Last 30 days
              </button>
              <button
                onClick={() => setDateRange({
                  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
                  endDate: new Date()
                })}
                className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                This month
              </button>
            </div>
          </div>
        </div>

        {/* Time Tracking Analytics */}
        <TimeTrackingAnalytics
          userRole="Client"
          userId={user?.userId}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />

        {/* Additional Report Options */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Reports</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Detailed Time Log</h4>
                  <p className="text-sm text-gray-600 mt-1">Complete list of all time entries with descriptions</p>
                </div>
                <FileText className="w-6 h-6 text-gray-400" />
              </div>
              <button className="mt-3 w-full px-4 py-2 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50">
                Generate Report
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Productivity Summary</h4>
                  <p className="text-sm text-gray-600 mt-1">Weekly and monthly productivity trends</p>
                </div>
                <BarChart3 className="w-6 h-6 text-gray-400" />
              </div>
              <button className="mt-3 w-full px-4 py-2 text-sm text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50">
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Understanding Your Reports</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>• <strong>Total Hours:</strong> All time tracked during the selected period</p>
            <p>• <strong>Billable Hours:</strong> Time marked as billable to clients</p>
            <p>• <strong>Productivity:</strong> Comparison with previous period to show trends</p>
            <p>• <strong>Top Tasks:</strong> Tasks where you spent the most time</p>
            <p>• <strong>Daily Breakdown:</strong> Day-by-day analysis of your time tracking</p>
          </div>
        </div>
      </div>
    </ClientDashboardLayout>
  );
};
